import React, { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import { useCustomerAuth } from './CustomerAuthContext';

export interface CartItem {
  id: number;
  variantId: number;     // 0 = no variant
  weightLabel: string;   // "" = no variant, "500g", "1 kg", etc.
  weightGrams: number;   // 0 = no variant; otherwise grams per item (250, 500, 1000…)
  name: string;
  price: number;
  qty: number;
  unit: string;
  emoji: string;
  image?: string;        // product primary image URL (falls back to emoji)
  farm: string;
  stock: number;         // always in base unit (kg)
  maxQty?: number;       // max kg per order (base unit)
  // Sold out while it sat in the cart. The line stays visible so the customer
  // sees what happened, but it is excluded from the total and never sent as an
  // order item — checkout is blocked until it's removed.
  outOfStock?: boolean;
  /** Bought for the next day, paid up front, non-refundable once confirmed. */
  isPreorder?: boolean;
}

type AddToCartItem = Omit<CartItem, 'qty' | 'variantId' | 'weightLabel' | 'weightGrams'> & {
  variantId?: number;
  weightLabel?: string;
  weightGrams?: number;
};

export interface CartAlert {
  removed: { name: string; emoji: string }[];
  adjusted: { name: string; emoji: string; oldQty: number; newQty: number }[];
  // Server's own words for a line it refused to save (out of stock, per-order
  // cap, deactivated product).
  rejected?: string[];
}

export interface ReorderResult {
  added: number;   // lines added to the cart
  capped: number;  // lines whose qty was reduced to fit stock / max cap
  skipped: number; // lines dropped (unavailable / no budget left)
}

interface CartContextType {
  items: CartItem[];
  addToCart: (item: AddToCartItem, amount?: number) => boolean;
  // Max items still addable for one variant line, budgeting the product's shared
  // stock + per-order kg cap against what its other variants already consume.
  variantLimit: (productId: number, variantId: number, stock: number, maxQty: number | undefined, weightGrams: number) => number;
  removeFromCart: (id: number, variantId?: number) => void;
  updateQty: (id: number, qty: number, variantId?: number) => void;
  clearCart: () => void;
  // Re-add a past order's items to the cart (server caps to stock + max order qty)
  reorder: (orderId: number) => Promise<ReorderResult>;
  cartAlerts: CartAlert | null;
  clearCartAlerts: () => void;
  // Lines that sold out while they sat in the cart — shown, but not orderable.
  outOfStockItems: CartItem[];
  // Orderable lines only: what checkout may send.
  orderableItems: CartItem[];
  removeOutOfStock: () => void;
  // Mark lines the server just rejected on stock, so the cart tells the same
  // story as the error the customer just saw.
  markOutOfStock: (lines: { product_id: number; variant_id?: number }[]) => void;
  total: number;
  count: number;
  syncing: boolean;
}

const API = process.env.REACT_APP_API_URL || '';

// A guest's cart lives only in this tab's memory otherwise, so a refresh — or
// following a link and coming back — emptied it. Persisted for guests ONLY: once
// signed in the server cart is the single truth, and replaying a stale local
// snapshot could resurrect lines the customer removed on another device.
const GUEST_CART_KEY = 'osw_guest_cart';

function loadGuestCart(): CartItem[] {
  try {
    const raw = localStorage.getItem(GUEST_CART_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Written by an older build, hand-edited, or half-written: keep only lines
    // that still have what the cart needs to render and to be sent to the server.
    return parsed
      .filter(i => i && Number(i.id) > 0 && Number(i.qty) > 0 && Number.isFinite(Number(i.price)))
      .map(i => ({
        ...i,
        id: Number(i.id),
        variantId: Number(i.variantId) || 0,
        qty: Number(i.qty),
        price: Number(i.price),
        // Missing stock would read as unlimited; 0 is the safe reading, and the
        // authoritative figure arrives with the next cart load anyway.
        stock: Number.isFinite(Number(i.stock)) ? Number(i.stock) : 0,
        weightGrams: Number(i.weightGrams) || 0,
        weightLabel: typeof i.weightLabel === 'string' ? i.weightLabel : '',
      }));
  } catch {
    return [];   // private mode, quota, corrupt JSON — start empty, never crash
  }
}

function saveGuestCart(items: CartItem[]) {
  try {
    if (items.length === 0) localStorage.removeItem(GUEST_CART_KEY);
    else localStorage.setItem(GUEST_CART_KEY, JSON.stringify(items));
  } catch { /* storage unavailable — the in-memory cart still works */ }
}

function cartKey(productId: number, variantId: number) {
  return `${productId}-${variantId}`;
}

// Base-unit (kg) a single line consumes
function lineKg(qty: number, weightGrams: number): number {
  return weightGrams > 0 ? qty * weightGrams / 1000 : qty;
}

// kg of a product already consumed by OTHER variant lines (stock + cap are shared
// across every variant of the same product, so they must be budgeted together)
function consumedByOthers(items: CartItem[], productId: number, excludeVariantId: number): number {
  return items
    .filter(i => i.id === productId && i.variantId !== excludeVariantId)
    .reduce((sum, i) => sum + lineKg(i.qty, i.weightGrams), 0);
}

// Max items allowed for one variant line, given shared kg-based stock + per-order
// kg cap, minus what other variants of the same product already consume.
function itemLimit(
  stock: number,
  maxQty: number | undefined,
  weightGrams: number,
  consumedOthersKg: number = 0,
): number {
  const perItemKg   = weightGrams > 0 ? weightGrams / 1000 : 1;
  const stockBudget = Math.max(0, stock - consumedOthersKg);
  const capBudget   = maxQty != null ? Math.max(0, maxQty - consumedOthersKg) : stockBudget;
  const budget      = Math.min(stockBudget, capBudget);
  // +epsilon so exact fractional weights (e.g. 0.1 kg) don't floor down a unit
  return Math.floor(budget / perItemKg + 1e-9);
}

// Saves a line to the server cart. `onReject` fires when the server refuses it
// — out of stock, per-order cap, product deactivated. fetch() does NOT throw on
// a 4xx, so without the res.ok check a rejection was silently discarded and the
// local cart kept an item the server never stored: it looked added until the
// next reload, then it was gone.
function dbUpsert(
  product_id: number, qty: number, token: string, variant_id: number = 0,
  onReject?: (message: string) => void,
) {
  fetch(`${API}/api/cart`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ product_id, qty, variant_id }),
  })
    .then(async res => {
      if (res.ok) return;
      const json = await res.json().catch(() => ({}));
      onReject?.(json.error || 'That item could not be added to your cart.');
    })
    .catch(() => { /* offline: local state stands, reconciled on next load */ });
}

function dbRemove(product_id: number, token: string, variant_id: number = 0) {
  fetch(`${API}/api/cart/${product_id}?variant_id=${variant_id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  }).catch(() => {});
}

function dbClear(token: string) {
  fetch(`${API}/api/cart`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  }).catch(() => {});
}

// Map a server cart row to a CartItem (shared by initial load, refresh and reorder)
function rowToItem(row: any): CartItem {
  return {
    id: row.id,
    variantId: Number(row.variant_id) || 0,
    weightLabel: row.weight_label || '',
    weightGrams: Number(row.weight_grams) || 0,
    name: row.name,
    price: Number(row.price),
    qty: Number(row.qty),
    unit: row.unit,
    emoji: row.emoji,
    image: row.image_url || undefined,
    farm: row.farm,
    // A pre-order has no stock; its ceiling is one slot's capacity. Reading
    // stock_qty here gave every pack a limit of 0, which pinned the cart's
    // quantity control at whatever it already held.
    stock: row.is_preorder
      ? (row.preorder_capacity_per_slot != null ? Number(row.preorder_capacity_per_slot) : 99)
      : (row.stock_qty != null ? Number(row.stock_qty) : 999),
    maxQty: row.max_order_qty != null ? Number(row.max_order_qty) : undefined,
    outOfStock: !!row.out_of_stock,
    isPreorder: !!row.is_preorder,
  };
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const { token, isAuthenticated } = useCustomerAuth();
  const [items, setItems] = useState<CartItem[]>(loadGuestCart);
  const [syncing, setSyncing] = useState(false);
  const [cartAlerts, setCartAlerts] = useState<CartAlert | null>(null);
  const clearCartAlerts = useCallback(() => setCartAlerts(null), []);

  const itemsRef = useRef<CartItem[]>([]);
  useEffect(() => { itemsRef.current = items; }, [items]);

  // Mirror a guest's cart to localStorage; hand it over to the server on login
  // and stop mirroring, so the DB is the only truth for a signed-in customer.
  useEffect(() => {
    if (isAuthenticated) saveGuestCart([]);
    else saveGuestCart(items);
  }, [items, isAuthenticated]);

  // The server refused a line we had already shown as added. Reconcile against
  // the DB — the item drops out immediately instead of at the next reload — and
  // record why, so the cart page can say what happened.
  const tokenRef = useRef<string | null>(null);
  useEffect(() => { tokenRef.current = token; }, [token]);
  const onServerReject = useCallback((message: string) => {
    setCartAlerts(prev => ({
      removed: prev?.removed || [],
      adjusted: prev?.adjusted || [],
      rejected: [...(prev?.rejected || []), message],
    }));
    // Re-read the cart rather than guessing what to undo. A rejection can mean
    // "that line was never stored" (a new item) or "it's stored, just not at
    // that quantity" (a bump past the stock), and only the server knows which —
    // pruning locally would delete a line the customer legitimately has, or the
    // sibling variant line of the same product.
    const t = tokenRef.current;
    if (!t) return;
    fetch(`${API}/api/cart`, { headers: { Authorization: `Bearer ${t}` } })
      .then(r => r.json())
      .then(json => { if (json.success) setItems(json.data.map(rowToItem)); })
      .catch(() => { /* offline: reconciles on the next load */ });
  }, []);

  // Only clear on an actual sign-out. Without the transition check this also
  // fired on first mount — every visitor's freshly restored guest cart wiped
  // before they ever saw it.
  const wasAuthedRef = useRef(false);
  useEffect(() => {
    const wasAuthed = wasAuthedRef.current;
    wasAuthedRef.current = !!(isAuthenticated && token);

    if (!isAuthenticated || !token) {
      if (wasAuthed) setItems([]);   // signed out — the server cart isn't theirs to see
      return;
    }

    const mergeAndLoad = async () => {
      setSyncing(true);
      try {
        const snapshot = itemsRef.current;

        if (snapshot.length > 0) {
          await Promise.all(
            snapshot.map(item =>
              fetch(`${API}/api/cart`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ product_id: item.id, qty: item.qty, variant_id: item.variantId }),
              }).catch(() => {})
            )
          );
        }

        const res = await fetch(`${API}/api/cart`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json();
        if (json.success) {
          setItems(json.data.map(rowToItem));
          const removed: CartAlert['removed'] = json.removed || [];
          const adjusted: CartAlert['adjusted'] = json.adjusted || [];
          setCartAlerts(removed.length > 0 || adjusted.length > 0 ? { removed, adjusted } : null);
        }
      } catch {
        // silently fall back to in-memory cart
      } finally {
        setSyncing(false);
      }
    };

    mergeAndLoad();
  }, [isAuthenticated, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const addToCart = useCallback((item: AddToCartItem, amount: number = 1): boolean => {
    const vId  = item.variantId  ?? 0;
    const wGrams = item.weightGrams ?? 0;
    const existing = itemsRef.current.find(i => i.id === item.id && i.variantId === vId);
    const currentQty = existing ? existing.qty : 0;
    const others = consumedByOthers(itemsRef.current, item.id, vId);
    const limit = itemLimit(item.stock ?? 999, item.maxQty, wGrams, others);
    if (currentQty >= limit) return false;
    const newQty = Math.min(currentQty + amount, limit);
    setItems(prev =>
      existing
        ? prev.map(i => (i.id === item.id && i.variantId === vId) ? { ...i, qty: newQty } : i)
        : [...prev, { ...item, variantId: vId, weightLabel: item.weightLabel ?? '', weightGrams: wGrams, qty: newQty }]
    );
    if (token) dbUpsert(item.id, newQty, token, vId, onServerReject);
    return true;
  }, [token, onServerReject]);

  const removeFromCart = useCallback((id: number, variantId: number = 0) => {
    setItems(prev => prev.filter(i => !(i.id === id && i.variantId === variantId)));
    if (token) dbRemove(id, token, variantId);
  }, [token]);

  const updateQty = useCallback((id: number, qty: number, variantId: number = 0) => {
    if (qty <= 0) {
      setItems(prev => prev.filter(i => !(i.id === id && i.variantId === variantId)));
      if (token) dbRemove(id, token, variantId);
      return;
    }
    const item = itemsRef.current.find(i => i.id === id && i.variantId === variantId);
    const others = consumedByOthers(itemsRef.current, id, variantId);
    const limit = itemLimit(item?.stock ?? 999, item?.maxQty, item?.weightGrams ?? 0, others);
    const capped = Math.min(qty, limit);
    setItems(prev => prev.map(i => (i.id === id && i.variantId === variantId) ? { ...i, qty: capped } : i));
    if (token) dbUpsert(id, capped, token, variantId, onServerReject);
  }, [token, onServerReject]);

  const variantLimit = useCallback(
    (productId: number, variantId: number, stock: number, maxQty: number | undefined, weightGrams: number) => {
      const others = consumedByOthers(items, productId, variantId);
      return itemLimit(stock ?? 999, maxQty, weightGrams, others);
    },
    [items],
  );

  const clearCart = useCallback(() => {
    setItems([]);
    if (token) dbClear(token);
  }, [token]);

  // Re-pull the authoritative cart from the server (used after a reorder)
  const refresh = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/api/cart`, { headers: { Authorization: `Bearer ${token}` } });
      const json = await res.json();
      if (json.success) {
        setItems(json.data.map(rowToItem));
        const removed: CartAlert['removed'] = json.removed || [];
        const adjusted: CartAlert['adjusted'] = json.adjusted || [];
        setCartAlerts(removed.length > 0 || adjusted.length > 0 ? { removed, adjusted } : null);
      }
    } catch {
      // keep current cart on failure
    }
  }, [token]);

  // Re-add a past order's items to the cart. The server caps each line to current
  // stock + the product's max_order_qty, then we reload the authoritative cart.
  const reorder = useCallback(async (orderId: number): Promise<ReorderResult> => {
    if (!token) return { added: 0, capped: 0, skipped: 0 };
    try {
      const res = await fetch(`${API}/api/cart/reorder`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_id: orderId }),
      });
      const json = await res.json().catch(() => ({}));
      await refresh();
      if (res.ok && json.success) {
        return { added: json.added || 0, capped: json.capped || 0, skipped: json.skipped || 0 };
      }
    } catch {
      // fall through to empty result
    }
    return { added: 0, capped: 0, skipped: 0 };
  }, [token, refresh]);

  // An out-of-stock line can't be bought, so it counts for nothing: not the
  // total, not the badge, not the min-order check, and never in the payload.
  const orderableItems = items.filter(i => !i.outOfStock);
  const outOfStockItems = items.filter(i => i.outOfStock);

  const removeOutOfStock = useCallback(() => {
    itemsRef.current.filter(i => i.outOfStock).forEach(i => {
      if (token) dbRemove(i.id, token, i.variantId);
    });
    setItems(prev => prev.filter(i => !i.outOfStock));
  }, [token]);

  const markOutOfStock = useCallback((lines: { product_id: number; variant_id?: number }[]) => {
    const keys = new Set(lines.map(l => cartKey(l.product_id, Number(l.variant_id) || 0)));
    setItems(prev => prev.map(i =>
      keys.has(cartKey(i.id, i.variantId)) ? { ...i, outOfStock: true, stock: 0 } : i
    ));
  }, []);

  const total = orderableItems.reduce((sum, i) => sum + i.price * i.qty, 0);
  const count = orderableItems.length; // distinct orderable items, not total qty

  return (
    <CartContext.Provider value={{ items, addToCart, variantLimit, removeFromCart, updateQty, clearCart, reorder, cartAlerts, clearCartAlerts, outOfStockItems, orderableItems, removeOutOfStock, markOutOfStock, total, count, syncing }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be inside CartProvider');
  return ctx;
};

// keep cartKey available for consumers that need a stable item key
export { cartKey };
